import { useState } from 'react';
import { useAuthStore } from '@/lib/stores/auth';
import { notifyApiError } from '@/lib/toast';
import { useCheckoutMutation } from '@/features/billing/queries';
import { getPaddle } from '@/features/billing/paddle';
import { CHECKOUT_ALLOWED_PAYMENT_METHODS } from '@/features/billing/paddle-constants';
import { isPlanNotPurchasable, planDisplayName } from '@/features/billing/plan-helpers';
import type { BillingPlan } from '@/lib/billing';
import { getToltReferral, TOLT_REFERRAL_CUSTOM_DATA_KEY } from '@/lib/tolt';

/**
 * The first-time-purchase half of the billing surface as a single hook: open the
 * checkout summary, then resolve the Paddle price and open the overlay.
 *
 * Extracted from `UpgradePlansCard` when the locked-feature upgrade prompt
 * (TRU-543) became a second caller — a free org has no Paddle subscription, so
 * `PATCH /billing/subscription` 041002s and EVERY unlock a free user attempts
 * lands here rather than on the in-place upgrade path. Copying the two handlers
 * into the prompt would have put the "which price id, which discount, which
 * customer email" decision in two places.
 *
 * The caller owns the rendering: it passes `checkoutPlan` to `<CheckoutDialog>`
 * and decides where `infoPlan` / `errorText` are shown, because the card and the
 * prompt place those banners very differently.
 */
export function usePaddleCheckout() {
  const email = useAuthStore((s) => s.user?.email) ?? undefined;
  const checkout = useCheckoutMutation();

  // All three are transient UI state — a live checkout isn't deep-linkable, so
  // none of it belongs in the URL.
  const [checkoutPlan, setCheckoutPlan] = useState<BillingPlan | null>(null);
  const [infoPlan, setInfoPlan] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  /**
   * Step 1: open the summary dialog so the customer can review the price and
   * optionally enter a promo code. There is no auto-apply, so a code typed there
   * is the ONLY way a discount reaches Paddle.
   */
  function start(plan: BillingPlan): void {
    setInfoPlan(null);
    setErrorText(null);
    setCheckoutPlan(plan);
  }

  /** Step 2: resolve the Paddle price and open the overlay. */
  async function proceed(plan: BillingPlan, discountId: string | null): Promise<void> {
    setCheckoutPlan(null);
    try {
      const paddle = await getPaddle();
      if (!paddle) return; // unconfigured — the CTA is already disabled
      const { priceId, customData } = await checkout.mutateAsync(plan.id);
      // The org's stored referral (first-touch, captured at signup) wins; the live
      // `window.tolt_referral` only fills the gap for orgs that predate the capture.
      const toltReferral = customData.tolt_referral ?? getToltReferral();
      paddle.Checkout.open({
        items: [{ priceId, quantity: 1 }],
        customData: {
          ...customData,
          ...(toltReferral ? { [TOLT_REFERRAL_CUSTOM_DATA_KEY]: toltReferral } : {}),
        },
        settings: { allowedPaymentMethods: [...CHECKOUT_ALLOWED_PAYMENT_METHODS] },
        ...(discountId ? { discountId } : {}),
        ...(email ? { customer: { email } } : {}),
      });
      setInfoPlan(planDisplayName(plan));
    } catch (err) {
      if (isPlanNotPurchasable(err)) {
        setErrorText("This plan isn't available for checkout yet.");
        return;
      }
      notifyApiError(err, 'Could not start checkout');
    }
  }

  function close(): void {
    setCheckoutPlan(null);
  }

  return {
    /** The plan whose checkout summary dialog is open; `null` when closed. */
    checkoutPlan,
    /** Display name of the plan whose Paddle overlay was just opened, or null. */
    infoPlan,
    /** Inline "this plan can't be bought right now" copy (040701), or null. */
    errorText,
    /** The plan id whose `POST /billing/checkout` is in flight, or null. */
    pendingPlanId: checkout.isPending ? (checkout.variables ?? null) : null,
    start,
    proceed,
    close,
  } as const;
}
